/**
 * Leitura do envelope de erro do gateway.
 *
 * O heimdall-api-platform responde erro assim:
 *
 * ```json
 * {
 *   "cause": { "errors": [{ "error": "PROCESSOR_CONFIG_CONFLICT", "message": "..." }] },
 *   "name": "ConflictError",
 *   "responseCode": 409,
 *   "requestId": "..."
 * }
 * ```
 *
 * A mensagem NÃO está em `data.message` — está aninhada em `cause.errors[]`. Ler o lugar errado
 * faz toda falha virar o texto genérico do fallback, escondendo do operador o que o backend
 * explicou.
 */

type GatewayError = {
  error?: string
  message?: string
}

type GatewayErrorBody = {
  message?: string
  responseCode?: number
  requestId?: string
  cause?: { errors?: GatewayError[] }
}

const bodyOf = (
  error: unknown
): GatewayErrorBody | null => {
  if (
    typeof error === "object" &&
    error !== null &&
    "response" in error
  ) {
    const response = (
      error as { response?: { data?: unknown } }
    ).response
    if (
      typeof response?.data === "object" &&
      response.data !== null
    ) {
      return response.data as GatewayErrorBody
    }
  }
  return null
}

/**
 * O status do erro, nas três formas em que ele chega.
 *
 * `response.status` é o caminho normal do axios. `statusCode` no topo aparece quando o erro já foi
 * normalizado antes de chegar aqui. E `responseCode` vem no próprio corpo do gateway — cobre o
 * caso de a resposta ser lida sem o status HTTP.
 */
const statusOf = (error: unknown): number | null => {
  if (typeof error === "object" && error !== null) {
    const status = (
      error as { response?: { status?: number } }
    ).response?.status
    if (typeof status === "number") return status

    const normalized = (error as { statusCode?: number })
      .statusCode
    if (typeof normalized === "number") return normalized
  }
  const code = bodyOf(error)?.responseCode
  return typeof code === "number" ? code : null
}

/** Mensagem que o backend explicou, ou o fallback quando não há nenhuma. */
export const getApiErrorMessage = (
  error: unknown,
  fallback: string
): string => {
  const body = bodyOf(error)
  const nested = body?.cause?.errors?.find(
    (item) => item.message
  )?.message
  return nested || body?.message || fallback
}

/**
 * O erro carrega este código de negócio.
 *
 * Preferir o código ao status: um mesmo 409 pode significar coisas diferentes, e o código diz
 * qual delas é.
 */
export const hasApiErrorCode = (
  error: unknown,
  code: string
): boolean =>
  Boolean(
    bodyOf(error)?.cause?.errors?.some(
      (item) => item.error === code
    )
  )

export const hasStatusCode = (
  error: unknown,
  status: number
): boolean => statusOf(error) === status

/** Id da requisição no gateway — o que se leva para investigar no log. */
export const getRequestId = (
  error: unknown
): string | null => bodyOf(error)?.requestId ?? null
